import authService from './authService';
import SecureCookieManager from '../utils/secureCookie';

import { getV2Token } from '../utils/v2AuthUtils';

let BASE_URL = process.env.REACT_APP_API_URL;

async function loadConfig() {
  try {
    const response = await fetch("/config.json");
    const config = await response.json();
    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    console.error("Failed to load config.json. Using default .env API URL", error);
  }
}

loadConfig().then(() => {});

export async function loginUser(username: string, password: string, rememberMe: boolean) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/auth/loginDemo`, {
      method: 'POST',
      body: JSON.stringify({ username, password, rememberMe }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        isSuccess: false,
        message: errorData.message || 'Login failed',
        failedLoginAttempts: errorData.failedLoginAttempts || 0,
      };
    }

    const data = await response.json();
    if (data.isSuccess) {
      SecureCookieManager.setAuthToken(data.data.token.accessToken, rememberMe);
    }
    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Login failed');
  }
}

export async function validateToken(token: string) {
  const response = await authService.apiCall(`${BASE_URL}/auth/validate-token`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    throw new Error('Failed to validate token');
  }

  const data = await response.json();
  if (data.isSuccess) {
    SecureCookieManager.setAuthToken(data.data.token.accessToken);
  }
  return data;
}

export async function getUsers(
  query: string,
  perPage: number,
  pageNo: number,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc'
): Promise<{ success: boolean; data?: any[]; totalCount?: number; message?: string }> {
  try {
    const params = new URLSearchParams({
      query: String(query ?? ''),
      perPage: String(perPage),
      pageNo: String(pageNo),
    });
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    // Match adib-frontend2 (users/index.tsx): GET /users?query&perPage&pageNo
    // (the backend @Get() on /users; there is no /users/regtech route).
    const response = await authService.apiCall(
      `${BASE_URL}/users?${params.toString()}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      const errorData = await response.json();
      return { success: false, message: errorData.message || 'Failed to get users' };
    }

    const data = await response.json();
    return data;
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to get users' };
  }
}

export async function getUserRolePermissions(userId?: string) {
  try {
    // The backend resolves the user from the auth token (like adib-frontend2's
    // permission endpoints), so no id is sent in the URL. Route: GET /.
    const response = await authService.apiCall(
      `${BASE_URL}/groups/getGroupUsers`,
      { method: 'GET' }
    );
    const data = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      // Surface status/message so the permission gates can tell an expired session or a
      // rejected CSRF token apart from a real denial (see utils/sessionExpiry).
      return { success: false, status: response.status, message: data?.message };
    }
    return data;
  } catch (error: any) {
    // A thrown exception here means the request never completed as a real HTTP
    // response — a network blip, a CSRF-token fetch failure, or apiCall's own
    // "Session expired" throw after a failed token refresh. That is NOT a real
    // permission answer. Without a status, isSessionFailure() can't tell this
    // apart from a genuine 403 from PermissionsGuard, so callers were painting
    // "You are not allowed to view this page" for what was actually a
    // transient failure. Report it like a dead session so callers redirect to
    // login and retry instead of showing a false denial.
    return { success: false, status: 401, message: error.message || 'Failed to get user role permissions' };
  }
}

export async function deleteUser(id: string): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/${id}`, { method: 'DELETE' });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { success: false, message: data.message || 'Failed to delete user' };
    }
    return data && data.success ? data : { success: true };
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to delete user' };
  }
}

export async function updateUserRole(id: string, data: Record<string, any>) {
  try {
    if (typeof data !== 'object' || data === null) {
      throw new Error('Invalid data provided to updateUserRole. Expected a JSON object.');
    }

    const response = await authService.apiCall(`${BASE_URL}/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update user role: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error updating user role:', error);
    throw new Error(error.message || 'An unknown error occurred.');
  }
}

export async function fetchPermissionsApi(page: any) {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/groups/permissions?page=${page}`,
      { method: 'GET' }
    );
    const data = await response.json().catch(() => ({} as any));
    if (!response.ok || !data.success) {
      // Carry the status through so callers can tell a dead session/CSRF failure (see
      // utils/sessionExpiry.isSessionFailure) apart from a genuine permission denial or a
      // transient server error — collapsing all three into one message is what previously
      // painted "You are not allowed to view this page" for failures that were not a real
      // permission answer.
      const err: any = new Error(data?.message || 'Failed to fetch');
      err.status = response.status;
      throw err;
    }
    return data;
  } catch (error: any) {
    if (typeof error?.status === 'number') throw error;
    const err: any = new Error(error?.message || error || 'Failed to fetch data');
    throw err;
  }
}

// --- User activation / assignment (match adib-frontend2: /user-functions/:id/*) ---
export async function updateUserGroupApi(userId: string, body: { groupId: string }) {
  const response = await authService.apiCall(`${BASE_URL}/user-functions/${userId}/group`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return await response.json();
}

export async function updateUserBranchesApi(userId: string, body: { branchIds: string[] }) {
  const response = await authService.apiCall(`${BASE_URL}/user-functions/${userId}/branches`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return await response.json();
}

export async function updateUserActiveApi(userId: string, body: { active: boolean }) {
  const response = await authService.apiCall(`${BASE_URL}/user-functions/${userId}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return await response.json();
}

export async function updateUserLockApi(userId: string, body: { isLocked: boolean }) {
  const response = await authService.apiCall(`${BASE_URL}/user-functions/${userId}/lock`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return await response.json();
}





export async function fetchAllUserDataApi(url: string, options: RequestInit = {}): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/${url}`, {
      ...options,
      method: (options.method as string) || 'GET',
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (response.status === 401 || response.status === 403 || data.message === 'Forbidden access') {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error?.message || error);
  }
}


/**
 * Groups that must never appear in a picker or the group manager.
 *
 * `admin_` is the PLATFORM operator group — the only one that crosses tenant isolation
 * (see new_adib_backend/src/auth/admin-groups.ts). Offering it in a tenant's own screens
 * invites someone to assign a user cross-tenant access, or to edit the permission row the
 * whole platform relies on, so it is filtered out here rather than at each call site.
 *
 * THIS LIST SAID 'super admin' UNTIL 2026-09-22. On that date the two admin names swapped
 * roles: the operator moved from `super_admin_` onto `admin_`, and `super_admin_` became
 * the administrator customers sign in as. Left unchanged, this would have hidden the one
 * group every tenant needs and exposed the one that must stay out of sight — so if you are
 * about to "restore" 'super admin' here, read admin-groups.ts first.
 *
 * Matching is normalised (lowercase, -/_ to space) because the same group is written
 * 'admin_' in the database and 'Admin' in older frontend literals. It is an exact match on
 * the normalised name, so `user_admin_` and `system_admin_` are unaffected.
 */
const HIDDEN_GROUP_NAMES = ['admin'];

const normalizeGroupName = (name?: unknown) =>
  String(name ?? '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();

export const isHiddenGroup = (name?: unknown) =>
  HIDDEN_GROUP_NAMES.includes(normalizeGroupName(name));

/** Drop hidden groups from a `{ success, data }` group response, leaving the rest intact. */
function stripHiddenGroups<T extends { data?: any[] }>(payload: T): T {
  if (!payload || !Array.isArray(payload.data)) return payload;
  return { ...payload, data: payload.data.filter((g: any) => !isHiddenGroup(g?.name)) };
}
export async function fetchAllGroupApi(): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/getAll`, { method: 'GET' });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (
        response.status === 401 ||
        response.status === 403 ||
        data.message === 'Forbidden access'
      ) {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to fetch');
    }
    return stripHiddenGroups(data);
  } catch (error: any) {
    throw new Error(error);
  }
}

export async function updatePermissionApi(id: string, body: any): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      if (
        response.status === 401 ||
        response.status === 403 ||
        data.message === 'Forbidden access'
      ) {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to update');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

export async function registerUsersBulk(users: any[]) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/register-users`, {
      method: 'POST',
      body: JSON.stringify({ users }),
    });
    const result = await response.json();
    if (!response.ok) {
      return { success: false, message: result.message || 'Failed to register users' };
    }
    return result;
  } catch (error: any) {
    const message = error?.response?.data?.message || error.message || 'Failed to register users';
    throw Object.assign(new Error(message), { response: error?.response });
  }
}

/**
 * The shape of a permission row, from the server: which pages exist, which action columns
 * they carry, and which pages are force-granted for every group.
 *
 * Asked for rather than hard-coded because `alwaysGranted` is the backend's rule
 * (completePermissions in page-catalog.ts), and a copy of it here would be one more place
 * to miss when it changes. A failure returns empty lists, which lock nothing — the screen
 * still renders.
 */
export async function fetchPageCatalog(): Promise<{ pages: string[]; actions: string[]; alwaysGranted: string[] }> {
  const empty = { pages: [], actions: [], alwaysGranted: [] };
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/page-catalog`, { method: 'GET' });
    if (!response.ok) return empty;
    const data = await response.json();
    if (!data?.success || !data?.data) return empty;
    return {
      pages: Array.isArray(data.data.pages) ? data.data.pages : [],
      actions: Array.isArray(data.data.actions) ? data.data.actions : [],
      alwaysGranted: Array.isArray(data.data.alwaysGranted) ? data.data.alwaysGranted : [],
    };
  } catch {
    return empty;
  }
}

/** Groups an admin can assign when creating a user (GET /groups/getAll). */
export async function fetchAllGroups(): Promise<{ success: boolean; data?: any[]; message?: string }> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/groups/getAll`, { method: 'GET' });
    const data = await response.json().catch(() => ({} as any));
    if (!response.ok) {
      return { success: false, status: response.status, message: data?.message } as any;
    }
    return stripHiddenGroups(data);
  } catch (error: any) {
    return { success: false, message: error.message || 'Failed to load groups' };
  }
}

export async function submitQuestionsAndAnswers(questionAnswers: any[]) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/publicSurvey/process`, {
      method: 'POST',
      body: JSON.stringify({ questionAnswers }),
    });
    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to submit questions and answers');
  }
}

export async function getUserInfo(): Promise<any> {
  try {
    const { validateToken } = await import('../utils/tokenValidator');
    const tokenFromCookieOrV2 = getV2Token() ;
    if (tokenFromCookieOrV2) {
      const userData = validateToken(tokenFromCookieOrV2);
      if (userData) {
        try {
          const response = await authService.apiCall(`${BASE_URL}/users/${userData.id}`, {
            method: 'GET',
          });
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const text = await response.text();
              if (text && text.trim() !== '') {
                const data = JSON.parse(text);
                return data.data || data;
              }
            }
          }
        } catch (backendError) {
          console.warn('Failed to fetch user from backend, using token data:', backendError);
        }
        return {
          id: userData.id,
          username: userData.username,
          name: userData.name,
          email: userData.email,
          title: userData.title,
          groupId: userData.group,
          group: { name: userData.groupName, permissions: userData.permissions },
          lastLogin: null,
        };
      }
    }

    // Cookie-only session (e.g. arrived here from the GRC frontend, so nothing was
    // written to this origin's sessionStorage by setV2Token). Must be /auth/me, NOT
    // /users/me: the users controller has no 'me' route, so /users/me falls through to
    // @Get(':id') with id="me", which finds nothing and answers 200 with an empty body.
    // The content-type check below then returned null, and every caller read that as
    // "not signed in" — RoleBasedRoute rendered "You are not allowed to view this page".
    const response = await authService.apiCall(`${BASE_URL}/auth/me`, { method: 'GET' });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) return null;
      return null;
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) return null;

    const text = await response.text();
    if (!text || text.trim() === '') return null;

    const data = JSON.parse(text);
    return data.data || data;
  } catch (error: any) {
    console.error('getUserInfo error:', error);
    return null;
  }
}

export async function getInfouserById(id: string) {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/${id}`, { method: 'GET' });
    const data = await response.json();

    if (!response.ok || !data) {
      if (
        response.status === 401 ||
        response.status === 403 ||
        data?.message === "Forbidden access"
      ) {
        throw new Error("Forbidden: You do not have permission to access this resource.");
      }
      throw new Error(data?.message || 'Failed to get user info');
    }

    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to get user info');
  }
}

export async function getAllOrganization() {
  try {
    const response = await authService.apiCall(`${BASE_URL}/tenant/tenant-ids`, {
      method: 'GET',
    });
    const data = await response.json();
    return data;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to get organizations');
  }
}

export const submitRegisterUser = async (data: any) => {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const result = await response.json();
    return result;
  } catch (error: any) {
    throw new Error(error.message || 'Failed to register user');
  }
};

// Superset guest token for the Compliance dashboard.
// Backend route: GET /users/guest-token5 -> { success, data: { token } }.
export async function getGuestToken5(): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/users/guest-token5`, {
      method: 'GET',
    });
    const data = await response.json();
    if (data?.success && data?.data?.token) {
      return data.data.token;
    }
    console.error('Failed to get guest token:', data?.error);
    throw new Error(data?.error || 'Failed to get guest token');
  } catch (error: any) {
    console.error('getGuestToken5 error:', error);
  }
}

/**
 * End the shared backend session.
 *
 * Deliberately does NOT precondition on getV2Token(). The session lives in the HttpOnly
 * d_c_c_t_p_* cookies, which JS cannot read — getV2Token() only sees the sessionStorage
 * mirror that setV2Token() writes when the user logs in through THIS app, and
 * sessionStorage is per-origin. So for anyone who arrived from the DCC (:5050 → :3001),
 * that mirror is empty and the old `if (!token) throw` bailed out before the fetch: local
 * storage got cleared and the user was sent to the login screen, but isSessionActive stayed
 * true and the auth cookies survived — so the DCC login saw a live session and bounced
 * straight back to home. Only the server can clear those cookies (see
 * AuthController.clearAuthCookie), so the request has to actually go out.
 *
 * The cookies are the credential; POST /auth/force-logout is guarded by JwtAuthGuard,
 * which reads them. Matches adib-frontend2's logoutApi().
 */
export async function logoutApi(): Promise<any> {
  try {
    const response = await authService.apiCall(`${BASE_URL}/auth/force-logout`, {
      method: 'POST',
    });

    const data = await response.json().catch(() => ({} as any));
    if (data.statusCode === 401 || data.statusCode === 403) {
      // Already signed out server-side (or the token was revoked). The cookies are cleared
      // either way, so report success rather than throwing the caller into its error path.
      return { success: true, data: 'Session already ended.' };
    }
    return data;
  } catch (error) {
    console.error('Error in logoutApi:', error);
    throw error;
  }
}

// Force-logout a specific user (super-admin only) — match adib-frontend2.
// Backend: POST /auth/force-logout-By-Admin with { userId }.
export async function logoutApiByAdmin(userId: string): Promise<any> {
  const response = await authService.apiCall(`${BASE_URL}/auth/force-logout-By-Admin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId }),
  });
  const data = await response.json();
  if (data.statusCode === 401 || data.statusCode === 403) {
    throw new Error("Forbidden: You do not have permission to access this resource.");
  }
  return data;
}
